import { Address, Participant } from "@/types/company";

export const getRole = (participant: Participant): string | null => {
  const hasAttributes = !!participant.role?.attributes.length;

  let value = "";

  switch (participant.role?.originalType) {
    case "ADM. DIR.":
      value = "Adm. dir.";
      break;
    case "FORMAND":
      value = "Formand";
      break;
    case "SUPPLEANT":
      value = "Suppleant";
      break;
    case "NÆSTFORMAND":
      value = "Næstformand";
      break;
    default:
      break;
  }

  if (hasAttributes) {
    const employeeElected =
      participant.role!.attributes.find((a) => a.type === "VALGFORM")?.value ===
      "medarbejdere i selskabet";
    if (employeeElected)
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      value ? (value += ", medarbejdervalgt") : (value = "Medarbejdervalgt");
  }

  return `${value}`;
};

export const formatAddress = (address: Address): string => {
  if (!address) return "";

  const parts: string[] = [];

  // Use pre-formatted address parts from backend if available
  if (address.address1) {
    parts.push(address.address1);
  } else {
    // Only build from components if formatted address1 is not available
    // Add care of name if present
    if (address.coName) {
      parts.push(`c/o ${address.coName}`);
    }

    // Build street address from components
    if (address.streetName) {
      let streetPart = address.streetName;

      if (address.houseNumberFrom) {
        streetPart += ` ${address.houseNumberFrom}`;
        if (address.houseLetterFrom) streetPart += address.houseLetterFrom;
      }

      if (address.floor) {
        streetPart += `, ${address.floor}.`;
        if (address.sideDoor) streetPart += ` ${address.sideDoor}`;
      }

      parts.push(streetPart);
    }
  }

  // Add address2 if available and not already included in previous parts
  if (address.address2 && !parts.some((part) => part === address.address2)) {
    parts.push(address.address2);
  }

  // If we don't have pre-formatted address parts, add postal code and city
  if (!address.address1 && !address.address2) {
    // Add postal code and city in Danish format
    if (address.postalCode || address.cityName) {
      let locationPart = address.postalCode ? `${address.postalCode}` : "";
      if (address.postalCode && address.cityName) locationPart += " ";
      if (address.cityName) locationPart += address.cityName;
      parts.push(locationPart);
    }
  }

  // Add postal box if available and not already included in formatted address parts
  const postalBoxStr = address.postalBox
    ? `Postboks ${address.postalBox}`
    : null;
  if (postalBoxStr && !parts.some((part) => part.includes(postalBoxStr))) {
    parts.push(postalBoxStr);
  }

  // Add freeText if available and not already included
  if (
    address.freeText &&
    !parts.some((part) => part.includes(address.freeText!))
  ) {
    parts.push(address.freeText);
  }

  // Add country if not Denmark and not already included
  if (address.countryCode && address.countryCode !== "DK") {
    const countryNames: Record<string, string> = {
      SE: "Sverige",
      NO: "Norge",
      DE: "Tyskland",
      GB: "Storbritannien",
      US: "USA",
      // Add more as needed
    };
    const countryName =
      countryNames[address.countryCode] || address.countryCode;
    if (!parts.some((part) => part.includes(countryName))) {
      parts.push(countryName);
    }
  }

  // Join all parts with commas
  return parts.join(", ");
};

export const cleanWebsiteUrl = (url: string): string => {
  if (!url) return "";

  try {
    // Handle cases where the URL doesn't have a protocol
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = "https://" + url;
    }

    // Parse the URL
    const urlObj = new URL(url);

    // Get the hostname and remove 'www.' prefix if present
    let domain = urlObj.hostname;
    if (domain.startsWith("www.")) {
      domain = domain.substring(4);
    }

    return domain;
  } catch (error) {
    // If URL parsing fails, try a regex approach
    const domainMatch = url.match(/^(?:https?:\/\/)?(?:www\.)?([^/?]+)/i);
    return domainMatch ? domainMatch[1] : url;
  }
};
